import { Pipe, PipeTransform, Inject, LOCALE_ID } from '@angular/core';
import { formatCurrency } from '@angular/common';
import { getCurrencySymbolWithFallback } from '@sg/common/src/lib/utils/currency-symbol.util';
import { abbreviateNumber, AbbreviationUnit } from './_abbreviate-number';

/**
 * Rendering mode for `SgSmartCurrencyPipe`. See `SgSmartCurrencyPipe` for
 * the selection guide and per-mode rules.
 */
export type SgCurrencyMode = 'full' | 'compact' | 'abbreviated';

/**
 * Formats a monetary amount. The caller picks the rendering mode based on
 * display surface.
 *
 * ## Mode selection
 *
 * | Mode            | When to use                                            | Example         |
 * |-----------------|--------------------------------------------------------|-----------------|
 * | `'full'`        | Detail views, transactions, statements, reconciliation | `$1,234,567.89` |
 * | `'abbreviated'` | Cards, KPIs, dashboard tiles, headline numbers         | `$1.23M`        |
 * | `'compact'`     | Chart axes, sparklines, dense tables                   | `$1.2M`         |
 *
 * ## Full mode rules
 *
 * - `abs(value) < $1M`: always 2dp, including trailing zeros (`$847.32`,
 *   `$847,234.00`).
 * - `abs(value) ≥ $1M`: 0–2dp, trailing zeros trimmed (`$847,500,000`,
 *   `$1,234,567.89`).
 *
 * ## Abbreviated mode rules — higher precision than compact (M/B/T = 2dp)
 *
 * - `$1K – $999K`: 1dp — but 0dp when the K-value is `≥100` (`$1.5K`,
 *   `$847K`).
 * - `$1M` and above: 2dp (`$1.24M`, `$847.5M`, `$1.24B`).
 * - Trailing zeros stripped (`12.00K` → `$12K`, `1.50K` → `$1.5K`).
 * - `$1 ≤ value < $1,000` render unabbreviated as integer (`$847`).
 * - `< $1` render as 2dp (`$0.05`, `$0.50`, `$0.00`).
 *
 * ## Compact mode rules — lower precision than abbreviated (M/B/T capped at 1dp)
 *
 * - 1dp max across all units — 0dp when the unit-value is `≥100` (`$1.2M`,
 *   `$15.5K`, `$847K`).
 * - Trailing zeros stripped (`1.0K` → `$1K`).
 * - `$1 ≤ value < $1,000` render unabbreviated as integer.
 * - `< $1` render as 2dp (`$0.05`, `$0.50`, `$0.00`) so sub-unit datasets
 *   don't collapse every value to `$0`.
 *
 * ## Special values
 *
 * - `null` / `undefined` / `NaN` / `±Infinity` → `—` (em dash, U+2014).
 * - Negative values use U+2212 minus before the symbol (`−$2,450.00`).
 * - ISO-code symbols get a trailing space (`CHF 1,234.50`); single-glyph
 *   symbols don't (`$847.32`, `€1.5K`).
 *
 * ## Usage
 *
 * Template:
 * ```html
 * {{ kpi.value | sgSmartCurrency: kpi.currency : 'abbreviated' }}
 * {{ transaction.amount | sgSmartCurrency: transaction.currency : 'full' }}
 * {{ row.totalRevenue | sgSmartCurrency: row.currency : 'compact' }}
 * ```
 *
 * Static (for ECharts axis formatters and other non-template callers):
 * ```ts
 * SgSmartCurrencyPipe.transform(1234567.89, 'USD', 'full');         // '$1,234,567.89'
 * SgSmartCurrencyPipe.transform(847234,     'USD', 'abbreviated');  // '$847K'
 * SgSmartCurrencyPipe.transform(1238472,    'USD', 'compact');      // '$1.2M'
 * ```
 */
@Pipe({
  name: 'sgSmartCurrency',
  standalone: false,
})
export class SgSmartCurrencyPipe implements PipeTransform {
  constructor(@Inject(LOCALE_ID) private readonly locale: string) {}

  transform(value: number | null | undefined, currencyCode: string, mode: SgCurrencyMode = 'full'): string {
    return SgSmartCurrencyPipe.transform(value, currencyCode, mode, this.locale);
  }

  /**
   * Static variant — same behavior, no DI required.
   *
   * @param value         Monetary value.
   * @param currencyCode  ISO-4217 currency code.
   * @param mode          Rendering mode. Defaults to `'full'`.
   * @param locale        BCP-47 locale code. Defaults to `'en-US'`.
   *
   * @example
   * SgSmartCurrencyPipe.transform(1234567.89, 'USD', 'full');         // '$1,234,567.89'
   * SgSmartCurrencyPipe.transform(847234,     'USD', 'abbreviated');  // '$847K'
   * SgSmartCurrencyPipe.transform(1238472,    'USD', 'compact');      // '$1.2M'
   * SgSmartCurrencyPipe.transform(1234.5,     'CHF', 'full');         // 'CHF 1,234.50'
   * SgSmartCurrencyPipe.transform(-2450,      'USD', 'full');         // '−$2,450.00'
   * SgSmartCurrencyPipe.transform(null,       'USD', 'full');         // '—'
   */
  static transform(
    value: number | null | undefined,
    currencyCode: string,
    mode: SgCurrencyMode = 'full',
    locale: string = 'en-US'
  ): string {
    if (value == null || !Number.isFinite(value)) {
      return '—';
    }

    const rawSymbol = getCurrencySymbolWithFallback(currencyCode, 'narrow', locale);
    // ISO-code fallback (e.g. 'CHF') gets a trailing space.
    const symbol = rawSymbol === currencyCode ? `${rawSymbol} ` : rawSymbol;

    if (mode === 'compact' || mode === 'abbreviated') {
      const sign = value < 0 ? '−' : '';

      return `${sign}${symbol}${formatCompactAbs(Math.abs(value), mode, locale)}`;
    }

    const digitsInfo = Math.abs(value) < 1_000_000 ? '1.2-2' : '1.0-2';

    return formatCurrency(value, locale, symbol, currencyCode, digitsInfo).replace(/-/g, '−');
  }
}

function formatCompactAbs(absValue: number, mode: 'compact' | 'abbreviated', locale: string): string {
  // Sub-unit values (< $1) keep 2dp in both compact and abbreviated so chart axes and KPI
  // tiles don't collapse every value to "$0" on sub-unit datasets.
  if (absValue < 1) return formatLocaleDecimal(absValue, 2, locale, 2);
  if (absValue < 1_000) return formatLocaleDecimal(absValue, 0, locale);

  return abbreviateNumber(absValue, (unitValue, unit) => decimalsFor(unitValue, unit, mode), locale);
}

// Locale-aware numeric formatting for the < $1000 (and sub-unit) paths of compact/abbreviated.
// `minDecimals = 0` (the default) auto-trims trailing zeros. `useGrouping: false` because
// these values are always < 1000 and never need a thousands separator.
function formatLocaleDecimal(value: number, maxDecimals: number, locale: string, minDecimals = 0): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
    useGrouping: false,
  }).format(value);
}

/**
 * Decimal-strategy callback for the shared `abbreviateNumber` helper. Encodes the
 * per-mode precision rule.
 *
 * | mode            | K range                | M / B / T range |
 * |-----------------|------------------------|-----------------|
 * | `'compact'`     | `≥100 → 0dp, else 1dp` | same tier rule  |
 * | `'abbreviated'` | `≥100 → 0dp, else 1dp` | always 2dp      |
 *
 * Modes diverge only in the M/B/T tiers (compact caps at 1dp, abbreviated keeps 2dp).
 * The K-range rule is identical between modes — that's why `SgEntityCountPipe` reuses the
 * compact strategy verbatim.
 */
function decimalsFor(unitValue: number, unit: AbbreviationUnit, mode: 'compact' | 'abbreviated'): number {
  if (mode === 'compact') {
    return unitValue >= 100 ? 0 : 1;
  }
  if (unit === 'K') {
    return unitValue >= 100 ? 0 : 1;
  }

  return 2;
}
